import { getAuth } from 'firebase/auth';
import type {
  GoogleAdsConnectionsResponse,
  GoogleAdsOAuthUrlResponse,
  GoogleAdsReportingAccount,
  GoogleAdsSelectionResponse,
} from '../types';

function getApiUrl(): string {
  if (import.meta.env.VITE_BUILDER_API_URL) return import.meta.env.VITE_BUILDER_API_URL;
  if (import.meta.env.VITE_AGENT_API_URL) return import.meta.env.VITE_AGENT_API_URL;

  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  if (hostname === 'localhost' || hostname === '127.0.0.1') return 'http://localhost:8081';
  if (hostname.includes('devsegmentlyai')) return 'https://dev-api.segmently.ai';
  if (hostname.includes('stagesegmentlyai')) return 'https://stage-api.segmently.ai';
  return 'https://api.segmently.ai';
}

async function errorMessage(response: Response, fallback: string): Promise<string> {
  const body = await response.json().catch(() => null) as {
    error?: string | { message?: string };
    message?: string;
  } | null;
  if (typeof body?.error === 'string' && body.error.trim()) return body.error;
  if (typeof body?.error === 'object' && body.error?.message?.trim()) return body.error.message;
  if (body?.message?.trim()) return body.message;
  return fallback;
}

export class GoogleAdsConnectService {
  constructor(private readonly apiUrl: string = getApiUrl()) {}

  private async authHeaders(): Promise<Record<string, string>> {
    const token = await getAuth().currentUser?.getIdToken();
    if (!token) throw new Error('User not authenticated');
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  private projectPath(projectId: string): string {
    return `/api/projects/${encodeURIComponent(projectId)}/integrations/google-ads`;
  }

  async listConnections(
    projectId: string,
    signal?: AbortSignal,
  ): Promise<GoogleAdsConnectionsResponse> {
    const response = await fetch(`${this.apiUrl}${this.projectPath(projectId)}/connections`, {
      headers: await this.authHeaders(),
      signal,
    });
    if (!response.ok) throw new Error(await errorMessage(response, 'Failed to load Google Ads connections'));
    return response.json();
  }

  async getOAuthUrl(
    projectId: string,
    connectionId: string,
    returnTo: string,
  ): Promise<string> {
    const params = new URLSearchParams({ connectionId, returnTo });
    const response = await fetch(
      `${this.apiUrl}${this.projectPath(projectId)}/oauth/authorize?${params}`,
      { headers: await this.authHeaders() },
    );
    if (!response.ok) throw new Error(await errorMessage(response, 'Failed to start Google Ads authorization'));
    return ((await response.json()) as GoogleAdsOAuthUrlResponse).url;
  }

  async listAccounts(
    projectId: string,
    connectionId: string,
    signal?: AbortSignal,
  ): Promise<GoogleAdsReportingAccount[]> {
    const params = new URLSearchParams({ connectionId });
    const response = await fetch(
      `${this.apiUrl}${this.projectPath(projectId)}/accounts?${params}`,
      { headers: await this.authHeaders(), signal },
    );
    if (!response.ok) throw new Error(await errorMessage(response, 'Failed to load Google Ads accounts'));
    return response.json();
  }

  async selectAccounts(input: {
    projectId: string;
    connectionId: string;
    customerIds: string[];
    managerByCustomerId: Record<string, string | undefined>;
  }): Promise<GoogleAdsSelectionResponse> {
    const response = await fetch(`${this.apiUrl}${this.projectPath(input.projectId)}/accounts/select`, {
      method: 'POST',
      headers: await this.authHeaders(),
      body: JSON.stringify({
        connectionId: input.connectionId,
        customerIds: input.customerIds,
        managerByCustomerId: input.managerByCustomerId,
      }),
    });
    if (!response.ok) throw new Error(await errorMessage(response, 'Failed to save Google Ads account selection'));
    return response.json();
  }

  async disconnect(projectId: string, connectionId: string): Promise<void> {
    const response = await fetch(`${this.apiUrl}${this.projectPath(projectId)}/disconnect`, {
      method: 'POST',
      headers: await this.authHeaders(),
      body: JSON.stringify({ connectionId }),
    });
    if (!response.ok) throw new Error(await errorMessage(response, 'Failed to disconnect Google Ads'));
  }
}

export interface GoogleAdsConnectApi {
  listConnections(projectId: string, signal?: AbortSignal): Promise<GoogleAdsConnectionsResponse>;
  getOAuthUrl(projectId: string, connectionId: string, returnTo: string): Promise<string>;
  listAccounts(
    projectId: string,
    connectionId: string,
    signal?: AbortSignal,
  ): Promise<GoogleAdsReportingAccount[]>;
  selectAccounts(input: {
    projectId: string;
    connectionId: string;
    customerIds: string[];
    managerByCustomerId: Record<string, string | undefined>;
  }): Promise<GoogleAdsSelectionResponse>;
  disconnect(projectId: string, connectionId: string): Promise<void>;
}

let singleton: GoogleAdsConnectService | null = null;

export function getGoogleAdsConnectService(): GoogleAdsConnectApi {
  if (!singleton) singleton = new GoogleAdsConnectService();
  return singleton;
}
